import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { changePassword } from '../../../api/auth.api';
import { Eye, EyeOff } from 'feather-icons-react/build/IconComponents';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import Header from '../../Header';
import Sidebar from '../../Sidebar';
import { useAuth } from '../../../contexts/AuthContext';
import { translateError } from '../../../utils/errorTranslations';

const ChangePassword = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    // Password visibility states
    const [oldPasswordVisible, setOldPasswordVisible] = useState(false);
    const [newPasswordVisible, setNewPasswordVisible] = useState(false);
    const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);

    // Live validation checks
    const getPasswordErrors = (password) => {
        const errors = [];
        if (password.length < 8) errors.push('поне 8 символа');
        if (!/[A-Z]/.test(password)) errors.push('поне една главна буква');
        if (!/[a-z]/.test(password)) errors.push('поне една малка буква');
        if (!/[0-9]/.test(password)) errors.push('поне една цифра');
        if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) errors.push('поне един специален символ');
        return errors;
    };

    const passwordErrors = newPassword ? getPasswordErrors(newPassword) : [];
    const passwordsMatch = confirmPassword ? newPassword === confirmPassword : true;
    const isFormValid = oldPassword && newPassword && confirmPassword && passwordErrors.length === 0 && passwordsMatch;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validate password requirements
        if (passwordErrors.length > 0) {
            setError('Паролата трябва да съдържа: ' + passwordErrors.join(', '));
            return;
        }

        // Validate passwords match
        if (!passwordsMatch) {
            setError('Новата парола и потвърждението не съвпадат');
            return;
        }

        // Validate old password is different from new
        if (oldPassword === newPassword) {
            setError('Новата парола трябва да е различна от старата');
            return;
        }

        setIsLoading(true);

        try {
            await changePassword({ oldPassword, newPassword });
            setSuccess(true);
            setOldPassword('');
            setNewPassword('');
            setConfirmPassword('');

            // Show success message for 3 seconds
            setTimeout(() => {
                setSuccess(false);
            }, 3000);
        } catch (err) {
            const message = err?.data?.message || err?.data?.error || 'Failed to change password';
            setError(translateError(message));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div>
            <Header />
            <Sidebar />

            <div className="page-wrapper">
                <div className="content">
                    {/* Page Header */}
                    <div className="page-header">
                        <div className="row">
                            <div className="col-sm-12">
                                <ul className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to={user?.role === 'PATIENT' ? '/telechat' : user?.role === 'DOCTOR' ? '/doctor-dashboard' : '/admin-dashboard'}>Начало</Link>
                                    </li>
                                    <li className="breadcrumb-item">
                                        <i className="feather-chevron-right">
                                            <FeatherIcon icon="chevron-right" />
                                        </i>
                                    </li>
                                    <li className="breadcrumb-item active">Промяна на парола</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    {/* /Page Header */}

                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-md-12">
                                            <div className="about-info">
                                                <h4>Промяна на парола</h4>
                                                <p className="text-muted" style={{ marginBottom: '30px' }}>
                                                    Актуализирайте вашата парола за по-добра сигурност
                                                </p>
                                            </div>

                                            {success && (
                                                <div className="alert alert-success alert-dismissible fade show" role="alert">
                                                    <strong>Успешно!</strong> Паролата ви беше променена.
                                                    <button type="button" className="btn-close" onClick={() => setSuccess(false)}></button>
                                                </div>
                                            )}

                                            {error && (
                                                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                                                    <strong>Грешка!</strong> {error}
                                                    <button type="button" className="btn-close" onClick={() => setError('')}></button>
                                                </div>
                                            )}

                                            <form onSubmit={handleSubmit}>
                                                <div className="row form-row">
                                                    <div className="col-md-6">
                                                        <div className="form-group local-forms">
                                                            <label>
                                                                Текуща парола <span className="text-danger">*</span>
                                                            </label>
                                                            <div style={{ position: 'relative' }}>
                                                                <input
                                                                    type={oldPasswordVisible ? 'text' : 'password'}
                                                                    className="form-control pass-input"
                                                                    value={oldPassword}
                                                                    onChange={(e) => setOldPassword(e.target.value)}
                                                                    required
                                                                    disabled={isLoading}
                                                                    placeholder="Въведете текущата парола"
                                                                />
                                                                <span
                                                                    className="toggle-password"
                                                                    onClick={() => setOldPasswordVisible(!oldPasswordVisible)}
                                                                    style={{ cursor: 'pointer', top: '50%', transform: 'translateY(-50%)' }}
                                                                >
                                                                    {oldPasswordVisible ? <EyeOff className="react-feather-custom" /> : <Eye className="react-feather-custom" />}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="row form-row">
                                                    <div className="col-md-6">
                                                        <div className="form-group local-forms">
                                                            <label>
                                                                Нова парола <span className="text-danger">*</span>
                                                            </label>
                                                            <div style={{ position: 'relative' }}>
                                                                <input
                                                                    type={newPasswordVisible ? 'text' : 'password'}
                                                                    className="form-control pass-input"
                                                                    value={newPassword}
                                                                    onChange={(e) => setNewPassword(e.target.value)}
                                                                    required
                                                                    disabled={isLoading}
                                                                    placeholder="Минимум 8 символа"
                                                                />
                                                                <span
                                                                    className="toggle-password"
                                                                    onClick={() => setNewPasswordVisible(!newPasswordVisible)}
                                                                    style={{ cursor: 'pointer', top: '50%', transform: 'translateY(-50%)' }}
                                                                >
                                                                    {newPasswordVisible ? <EyeOff className="react-feather-custom" /> : <Eye className="react-feather-custom" />}
                                                                </span>
                                                            </div>
                                                            {newPassword && passwordErrors.length > 0 ? (
                                                                <small className="text-danger">
                                                                    Липсва: {passwordErrors.join(', ')}
                                                                </small>
                                                            ) : (
                                                                <small className="form-text text-muted">
                                                                    Изисквания: минимум 8 символа, главна и малка буква, цифра и специален символ
                                                                </small>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="col-md-6">
                                                        <div className="form-group local-forms">
                                                            <label>
                                                                Потвърдете новата парола <span className="text-danger">*</span>
                                                            </label>
                                                            <div style={{ position: 'relative' }}>
                                                                <input
                                                                    type={confirmPasswordVisible ? 'text' : 'password'}
                                                                    className="form-control pass-input"
                                                                    value={confirmPassword}
                                                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                                                    required
                                                                    disabled={isLoading}
                                                                    placeholder="Повторете новата парола"
                                                                />
                                                                <span
                                                                    className="toggle-password"
                                                                    onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}
                                                                    style={{ cursor: 'pointer', top: '50%', transform: 'translateY(-50%)' }}
                                                                >
                                                                    {confirmPasswordVisible ? <EyeOff className="react-feather-custom" /> : <Eye className="react-feather-custom" />}
                                                                </span>
                                                            </div>
                                                            {confirmPassword && !passwordsMatch && (
                                                                <small className="text-danger">
                                                                    Паролите не съвпадат
                                                                </small>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="submit-section" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                                    <button
                                                        type="submit"
                                                        className="btn btn-primary"
                                                        disabled={isLoading || !isFormValid}
                                                        style={{ minWidth: '150px', flex: '0 1 auto' }}
                                                    >
                                                        {isLoading ? 'Запазване...' : 'Запази промените'}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary"
                                                        onClick={() => navigate(user?.role === 'PATIENT' ? '/telechat' : user?.role === 'DOCTOR' ? '/doctor-dashboard' : '/admin-dashboard')}
                                                        disabled={isLoading}
                                                        style={{ minWidth: '100px', flex: '0 1 auto' }}
                                                    >
                                                        Отказ
                                                    </button>
                                                </div>
                                            </form>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChangePassword;
